import React from 'react';
import { graphql, PageProps } from 'gatsby';
import SEO from '../components/Seo';
import ProductDetailAdvanced from '../components/product/detail/advanced/ProductDetailAdvanced';
import ProductDetailEasy from '../components/product/detail/easy/ProductDetailEasy';
import { ProductPageLayout } from '../components/product/ProductPageLayout';
import { ProductPageSelectorExperiment } from '../components/product/ProductPageSelectorExperiment';
import type { IPageData, IPortalConfiguration, IProductPage } from '../types/product/productDetailPage';
import type { IPageContext } from '../types/gatsby';
import '../css/product-page.css';
import ProductDetailPPAG from '../components/product/detail/PPAG/ProductDetailPPAG';

export const query = graphql`
fragment Attributes on ContentfulAttribute {
  pimAttributeName
  contentful_id
  attributeAlias
  attributeValue {
    pimAttributeValue
    attributeValueAlias
    attributeValueImage {
      contentful_id
      url
      title
    }
    supportArticle {
      id: contentful_id
      title
      summary: childContentfulSupportArticlePageSummaryTextNode {
        childMarkdownRemark {
          html
        }
      }
      path
      heroImage {
        id: contentful_id
        title
        url
      }
      faqs {
        ...LinkedFaq
      }
      gallery {
        images {
          title
          url
        }
        enlargedImages: images {
          title
          url
        }
        thumbnails: images {
          title
          url
        }
      }
    }
  }
  supportArticle {
    id: contentful_id
    title
    summary: childContentfulSupportArticlePageSummaryTextNode {
      childMarkdownRemark {
        html
      }
    }
    path
    heroImage {
      id: contentful_id
      title
      url
    }
    faqs {
      ...LinkedFaq
    }
    gallery {
      images {
        id: contentful_id
        title
        url
      }
      enlargedImages: images {
        id: contentful_id
        title
        url
      }
      thumbnails: images {
        id: contentful_id
        title
        url
      }
    }
  }
}

fragment Selector on ContentfulProductSelector {
  __typename
  contentful_id
  product {
    contentful_id
    key
    mcpSku
    productName
    productNameOriginal
    customProductQuantities
    attributes {
      ...Attributes
    }
  }
  selectionMode
  configuration {
    variantSku
    attributes {
      id
      onReviewPage
      hasSizesDiffer
      name
      groupTitle {
        nl_NL
        nl_BE
        fr_BE
      }
      pimName
      type
      template
      defaultValue
      invalidValuesVisibility
      attributes {
        id
        name
        pimName
        type
        template
        defaultValue
        range {
          unitOfMeasure
        }
      }
      values {
        id
        name
        label {
          type
        }
        enabled
      }
      grid {
        invalidValuesVisibility
        column {
          id
          name
          type
          values {
            name
            id
          }
        }
        row {
          id
          name
          type
          values {
            name
            id
          }
        }
      }
      isConditionalAttribute
      conditionalAttributeRules
    }
  }
}

fragment Service on ContentfulService {
  key
  name
  displayName
  shortDescription
  description {
    description
  }
  readMore {
    ... on ContentfulSupportArticlePage {
      path
      contentful_id
    }
    ... on ContentfulPageSystem {
      path
      contentful_id
    }
    ... on ContentfulPageContainer {
      path
      contentful_id
    }
  }
}

query($locale: String!, $contentfulId: String!) {
  pageContent: contentfulProductContent(
    contentful_id: { eq: $contentfulId },
    node_locale: { eq: $locale }
  ) {
    contentful_id
    paths
    reviewPath
    productName
      labels {
      type
      displayName
    }
    productCategory {
      categoryName
      path
    }
    title
    subtitle
    introduction {
      introduction
    }
    productDiscounts
    description {
      childMarkdownRemark {
        excerpt(pruneLength: 50000, format: PLAIN)
      }
      description
    }
    services {
      ...Service
    }
    productReviews(locale: $locale)
    pageLayout
    resolvedSelectors
    selectorExperiment
    productRoutes {
      id: contentful_id
      routeIdentifier
      productCode {
        internal {
          content
        }
      }
      label {
        type
        displayName
      }
      productInfo
      isDefault
    }
    productTabs {
      title: displayTitle
      type
      content {
        content
      }
    }
    linkedFaqs {
      ...LinkedFaq
    }
    productUploadSpecifications {
      uploadSpecifications {
        id: contentful_id
        title
        file {
          url
          fileName
          contentType
        }
      }
    }
    featuredImage {
      url
    }
    gallery {
      ... on ContentfulGallery {
        ...Gallery
      }
      ... on ContentfulExperiment {
        experimentId
        control {
          ...Gallery
        }
        variant {
          ...Gallery
        }
      }
    }
    seoTitle
    seoDescription {
      seoDescription
    }
    seoRobots {
      seoRobots
    }
    openGraphImage {
      ...OpenGraphImage
    }
    productQuote {
      title
      buttonKind
      buttonText
      buttonUrl
      customerGroupId
      content {
        content
      }
      quotePdpImage {
        url
        title
      }
    }
    scenes
    reviews(locale: $locale)
  }
  portalConfiguration {
    channel {
      name
      siteUrl
    }
    defaultShippingCountry,
    defaultPostalCode,
    currencies
  }
}
`;

interface Props {
  pageContent: IProductPage & IPageData;
  portalConfiguration: IPortalConfiguration,
}

const ProductPage = ({ data, pageContext }: PageProps<Props, IPageContext>) => {
  const { pageContent } = data;
  const { pageLayout, resolvedSelectors, selectorExperiment } = pageContent;

  // Determine if we should use the experiment
  const isSelectorExperiment = selectorExperiment
    && selectorExperiment.experimentId
    && selectorExperiment.variant
    && selectorExperiment.control;

  // Selector Experiment
  if (isSelectorExperiment) {
    return (
      <ProductPageSelectorExperiment
        data={data}
        localizedPaths={pageContext.localizedPaths}
        experimentId={selectorExperiment.experimentId}
      />
    );
  }

  // Get the resolved selector if we are not using the experiment
  const selector = resolvedSelectors;

  // Easy Layout
  if (pageLayout === 'Easy') {
    return (
      <ProductPageLayout
        pageData={data}
        localizedPaths={pageContext.localizedPaths}
        selector={selector}
        pageContent={pageContent}
      >
        <ProductDetailEasy selector={selector} data={data} />
      </ProductPageLayout>
    );
  }

  // PPAG Layout
  if (pageLayout === 'PPAG') {
    return (
      <ProductPageLayout
        pageData={data}
        localizedPaths={pageContext.localizedPaths}
        selector={selector}
        pageContent={pageContent}
      >
        <ProductDetailPPAG selector={selector} data={data} />
      </ProductPageLayout>
    );
  }

  // Advanced Layout
  return (
    <ProductPageLayout
      pageData={data}
      localizedPaths={pageContext.localizedPaths}
      selector={selector}
      pageContent={pageContent}
    >
      <ProductDetailAdvanced data={data} productPage={pageContent} selector={selector} />
    </ProductPageLayout>
  );
};

export const Head = ({ data, pageContext }: PageProps<Props, IPageContext>) => {
  const content = data.pageContent;
  return (
    <SEO
      locale={pageContext.locale}
      title={content.seoTitle}
      description={content.seoDescription?.seoDescription}
      robots={content.seoRobots?.seoRobots}
      alternates={pageContext.alternates}
      image={content.openGraphImage?.url}
    />
  );
};

export default ProductPage;
